import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ArrowLeft, ArrowRight } from "lucide-react";

const SLIDES = [
  {
    tag: "Capital · Advisory",
    title: "Anchored capital for the African century",
    sub: "From Pretoria to Lagos, Nairobi to Accra — we deploy intelligent capital where the continent grows fastest.",
    img: "/generated/carousel-1-capital.png",
  },
  {
    tag: "Telecom · Fintech",
    title: "Digital infrastructure as a foundation",
    sub: "MTN Group's pivot from voice to digital ecosystems — financial inclusion at continental scale.",
    img: "/generated/carousel-2-telecom.png",
  },
  {
    tag: "Mining · Critical Minerals",
    title: "Platinum group metals at the green transition",
    sub: "Valterra Platinum — leading primary producer of catalysts powering hydrogen and clean engines.",
    img: "/generated/carousel-3-mining.png",
  },
  {
    tag: "Property · Real Estate",
    title: "Master developers of mixed-use precincts",
    sub: "Attacq's R28bn portfolio — including Waterfall City, one of Africa's flagship developments.",
    img: "/generated/carousel-4-property.png",
  },
  {
    tag: "Logistics",
    title: "Bridging trade-corridor gaps",
    sub: "Port logistics, warehousing and last-mile delivery — the unsung backbone of African commerce.",
    img: "/generated/carousel-5-logistics.png",
  },
  {
    tag: "Financial Services",
    title: "Capital allocators to the capital allocators",
    sub: "Banks, insurers, and alternative lenders — we partner to expand SME access and regulatory capacity.",
    img: "/generated/carousel-6-financial.png",
  },
  {
    tag: "Infrastructure Development",
    title: "Building Africa's foundation",
    sub: "Transport corridors, water utilities, social infrastructure — the projects that compound prosperity.",
    img: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/7r2w3g2l_ed7ec24d-a0c8-4df0-94e0-79dc61bde7e1.png",
  },
  {
    tag: "Renewable Energy",
    title: "Powering a just energy transition",
    sub: "Utility-scale solar, wind and storage — clean, dispatchable and bankable across SADC.",
    img: "https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/nk4z2m9j_94e38f4a-4279-4e18-8ade-0fb9fd60153b.png",
  },
];

export default function HeroCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", duration: 35 },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  const [selected, setSelected] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onSelect = useCallback((api) => setSelected(api.selectedScrollSnap()), []);
  const onInit = useCallback((api) => setScrollSnaps(api.scrollSnapList()), []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi?.scrollTo(i), [emblaApi]);

  return (
    <section
      data-testid="hero-carousel"
      className="relative section py-0"
      style={{ background: "var(--bg)", borderBottom: "1px solid var(--border-subtle)" }}
    >
      <div className="container-x pt-20 md:pt-24">
        <div className="flex items-end justify-between gap-8 mb-10 md:mb-14">
          <div className="js-reveal">
            <span className="overline">— Portfolio in Motion</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tight">
              The continent's <em className="italic" style={{ color: "var(--gicp-gold)" }}>investable</em> spine.
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={prev}
              data-testid="carousel-prev"
              aria-label="Previous slide"
              className="w-12 h-12 inline-flex items-center justify-center border transition-colors hover:border-[var(--gicp-gold)] hover:text-[var(--gicp-gold)]"
              style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
            >
              <ArrowLeft size={16} />
            </button>
            <button
              onClick={next}
              data-testid="carousel-next"
              aria-label="Next slide"
              className="w-12 h-12 inline-flex items-center justify-center border transition-colors hover:border-[var(--gicp-gold)] hover:text-[var(--gicp-gold)]"
              style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden" ref={emblaRef} data-testid="carousel-track">
        <div className="flex container-x">
          {SLIDES.map((s, i) => (
            <div
              key={i}
              data-testid={`carousel-slide-${i}`}
              className="relative flex-[0_0_85%] sm:flex-[0_0_55%] md:flex-[0_0_38%] lg:flex-[0_0_25%] xl:flex-[0_0_24%] pr-4 md:pr-6"
            >
              <div className="relative aspect-[3/4] overflow-hidden group flex flex-col">
                <div className="relative flex-1 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                    style={{ filter: "saturate(1.05) contrast(1.04)" }}
                  />
                  {/* Slide index — all white */}
                  <div className="absolute top-4 right-4 text-[10px] tracking-[0.28em] uppercase tabular" style={{ color: "#FDFBF7" }}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <span className="opacity-70"> / {String(SLIDES.length).padStart(2, "0")}</span>
                  </div>
                </div>

                {/* Solid black text panel — contains both tag and title */}
                <div className="p-5 md:p-6" style={{ background: "#050914" }}>
                  <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "var(--gicp-gold)" }}>
                    {s.tag}
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl mt-2 leading-[1.15] tracking-tight min-h-[3rem]" style={{ color: "#FDFBF7" }}>
                    {s.title}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination + progress */}
      <div className="container-x pt-8 pb-20 md:pb-24 flex items-center justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-2" data-testid="carousel-dots">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              data-testid={`carousel-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className="group h-1 transition-all duration-500"
              style={{
                width: selected === i ? 56 : 24,
                background: selected === i ? "var(--gicp-gold)" : "var(--border-subtle)",
              }}
            />
          ))}
        </div>
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={prev}
            aria-label="Previous"
            className="w-10 h-10 inline-flex items-center justify-center border"
            style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
          >
            <ArrowLeft size={14} />
          </button>
          <button
            onClick={next}
            aria-label="Next"
            className="w-10 h-10 inline-flex items-center justify-center border"
            style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
          >
            <ArrowRight size={14} />
          </button>
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase invisible" style={{ color: "var(--fg-soft)" }}>
          Auto · 5s · Hover to pause
        </div>
      </div>
    </section>
  );
}
